import { Injectable } from '@angular/core';
import { INotificationsHub as INotificationsHub } from './i-notifications.hub';
import { SignalRHub as BaseSignalrHub } from '../common/hubs/signalr.hub';
import { map, merge, Observable, Subject } from 'rxjs';
import { SignalRConnectionManager } from '../common/hubs/signalr-connection.manager';
import { INotificationMessage } from '../models/notification.message.model';

/** A notification the backend recorded for the signed-in user, pushed live as `ReceiveNotification`. */
export interface IRealtimeNotification {
    id: string;
    createdOnUtc: string;
    channel: string;
    sourceType: string | null;
    sourceId: string | null;
    title: string | null;
    body: string;
    data: Record<string, string> | null;
}

@Injectable({ providedIn: 'root' })
export class NotificationsHub extends BaseSignalrHub implements INotificationsHub {
    public messageReceived$: Observable<INotificationMessage<any>>;

    constructor(connectionManager: SignalRConnectionManager) {
        let messageRecievedSubject = NotificationsHub.ReceiveAsync();
        let notificationReceivedSubject = new Subject<IRealtimeNotification>();
        super({
            connectionManager,
            hubEvents: [
                ...NotificationsHub.createHubEvents(messageRecievedSubject),
                { name: 'ReceiveNotification', subject: notificationReceivedSubject }
            ]
        });
        this.messageReceived$ = merge(
            messageRecievedSubject.asObservable(),
            notificationReceivedSubject.pipe(map(NotificationsHub.toNotificationMessage))
        );
    }

    public joinGroups(groups: string[]): Observable<void> {
        return this.invokeMethod(this.joinGroups.name, groups);
    }

    public leaveGroups(groups: string[]): Observable<void> {
        return this.invokeMethod(this.leaveGroups.name, groups);
    }

    private static createHubEvents(subject: Subject<INotificationMessage<any>>) {
        return [
            {
                name: NotificationsHub.ReceiveAsync.name,
                subject: subject
            }
        ];
    }

    private static ReceiveAsync(): Subject<INotificationMessage<any>> {
        return new Subject<INotificationMessage<any>>();
    }

    private static toNotificationMessage(notification: IRealtimeNotification): INotificationMessage<any> {
        return {
            id: notification.id,
            type: notification.sourceType ?? notification.channel,
            title: notification.title ?? '',
            details: notification.body,
            content: { sourceId: notification.sourceId, ...(notification.data ?? {}) },
            notifier: notification.channel,
            createdOn: notification.createdOnUtc,
            isReaded: false
        };
    }
}
